import React, { useEffect, useRef } from "react";
import { connect, useStore } from "react-redux";
import { Field, reduxForm, initialize, change, formValueSelector } from "redux-form";
import { SelectArea } from "../../../../../../../../../../gpap-lib/components/shared-components/renderer";
import MappedText from "../../../../../../../../../study-page/create-study/analysis-page/components/clinical-info/mapText";
import { GPAP_Button_Light } from "../../../../../../../../../../gpap-lib/components/Button_Collection";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import {
    GERMLINE_SAMPLE_DEFAULT_DP,
    GERMLINE_SAMPLE_DEFAULT_GQ
} from "../../../../../../../../../study-page/create-study/analysis-page/formatSamples";

const FORM = "dir_germline_gt";

const GT_OPTIONS = [
    { value: "0/1", label: "0/1" },
    { value: "1/1", label: "1/1" }
];

/** Passed to initialize() on reset (Genomics / FilterDialog / FilterPanel). */
export const DIR_GERMLINE_GT_FORM_RESET = {
    gt_selection: [],
    dir_germline_dp: GERMLINE_SAMPLE_DEFAULT_DP,
    dir_germline_gq: GERMLINE_SAMPLE_DEFAULT_GQ,
    dir_germline_ad_low: null,
    dir_germline_ad_high: null
};

function normalizeGtCodes(gtSel) {
    if (!Array.isArray(gtSel) || gtSel.length === 0) return [];
    return [
        ...new Set(
            gtSel
                .map((x) => (typeof x === "string" ? x : x?.value))
                .filter((x) => x === "0/1" || x === "1/1")
        )
    ];
}

function gtSelectionKey(codes) {
    return JSON.stringify([...codes].sort());
}

/** Sorted key for both 0/1 and 1/1 selected — used to detect dual → single transitions. */
const DUAL_01_11_KEY = gtSelectionKey(["0/1", "1/1"]);

function DirNumberField({ input, label, helperText, meta, inputProps, disabled }) {
    const v = input.value;
    const strVal = v === null || v === undefined || v === "" ? "" : String(v);
    return (
        <TextField
            type="number"
            size="small"
            fullWidth
            disabled={Boolean(disabled)}
            label={label}
            helperText={meta.touched && meta.error ? meta.error : helperText}
            error={meta.touched && Boolean(meta.error)}
            value={strVal}
            onChange={(e) => {
                const raw = e.target.value;
                input.onChange(raw === "" ? null : raw);
            }}
            onBlur={input.onBlur}
            inputProps={inputProps}
            style={{ fontSize: "14px", marginTop: 8 }}
        />
    );
}

const dirGermlineSelector = formValueSelector(FORM);

function DirGermlineGtSection(props) {
    const { handleSubmit, pristine, submitting, default_filters, dispatch, gt_selection } = props;
    const store = useStore();

    const dirFilterSyncKey = default_filters
        ? JSON.stringify({
              gt: default_filters.dir_germline_gt ?? null,
              dp: default_filters.dir_germline_dp ?? null,
              gq: default_filters.dir_germline_gq ?? null,
              adl: default_filters.dir_germline_ad_low ?? null,
              adh: default_filters.dir_germline_ad_high ?? null
          })
        : "null";

    const prevGtKeyRef = useRef(undefined);
    const suppressNextZygosityAdEffectRef = useRef(false);

    useEffect(() => {
        if (!default_filters) return;

        const raw = default_filters.dir_germline_gt;
        let gt_selection_sync = [];
        if (Array.isArray(raw) && raw.length > 0) {
            gt_selection_sync = GT_OPTIONS.filter((o) => raw.includes(o.value));
        }
        const codes = normalizeGtCodes(gt_selection_sync);
        let adLow = default_filters.dir_germline_ad_low ?? null;
        let adHigh = default_filters.dir_germline_ad_high ?? null;
        if (codes.length > 1) {
            adHigh = null;
        } else if (codes.length === 1 && adLow == null && adHigh == null) {
            if (codes[0] === "0/1") {
                adLow = 0.2;
                adHigh = 0.8;
            } else if (codes[0] === "1/1") {
                adLow = 0.8;
                adHigh = 1;
            }
        }
        dispatch(
            initialize(FORM, {
                gt_selection: gt_selection_sync,
                dir_germline_dp: default_filters.dir_germline_dp ?? GERMLINE_SAMPLE_DEFAULT_DP,
                dir_germline_gq: default_filters.dir_germline_gq ?? GERMLINE_SAMPLE_DEFAULT_GQ,
                dir_germline_ad_low: adLow,
                dir_germline_ad_high: adHigh
            })
        );
        prevGtKeyRef.current = gtSelectionKey(codes);
        suppressNextZygosityAdEffectRef.current = true;
    }, [dirFilterSyncKey, default_filters, dispatch]);

    useEffect(() => {
        if (suppressNextZygosityAdEffectRef.current) {
            suppressNextZygosityAdEffectRef.current = false;
            return;
        }

        const state = store.getState();
        const ad_low = dirGermlineSelector(state, "dir_germline_ad_low");
        const ad_high = dirGermlineSelector(state, "dir_germline_ad_high");
        const gtCodes = normalizeGtCodes(gt_selection);
        const key = gtSelectionKey(gtCodes);
        const prevKey = prevGtKeyRef.current;

        const clearAd = () => {
            dispatch(change(FORM, "dir_germline_ad_low", null));
            dispatch(change(FORM, "dir_germline_ad_high", null));
        };

        if (gtCodes.length > 1) {
            dispatch(change(FORM, "dir_germline_ad_high", null));
            if (
                ad_low === null ||
                ad_low === undefined ||
                ad_low === ""
            ) {
                dispatch(change(FORM, "dir_germline_ad_low", 0.2));
            }
            prevGtKeyRef.current = key;
            return;
        }

        if (gtCodes.length === 0) {
            if (prevKey !== undefined && prevKey !== "[]") {
                clearAd();
            }
            prevGtKeyRef.current = key;
            return;
        }

        const adEmpty =
            (ad_low === null || ad_low === undefined || ad_low === "") &&
            (ad_high === null || ad_high === undefined || ad_high === "");
        const transitioned = prevKey !== undefined && prevKey !== key;
        const shouldApplyBand = transitioned || (prevKey === undefined && adEmpty);
        const fromDualHethomToSingle =
            transitioned && String(prevKey) === DUAL_01_11_KEY && gtCodes.length === 1;

        if (fromDualHethomToSingle) {
            if (gtCodes[0] === "0/1") {
                if (ad_low === null || ad_low === undefined || ad_low === "") {
                    dispatch(change(FORM, "dir_germline_ad_low", 0.2));
                }
                if (ad_high === null || ad_high === undefined || ad_high === "") {
                    dispatch(change(FORM, "dir_germline_ad_high", 0.8));
                }
            } else if (gtCodes[0] === "1/1") {
                if (ad_low === null || ad_low === undefined || ad_low === "") {
                    dispatch(change(FORM, "dir_germline_ad_low", 0.8));
                }
                if (ad_high === null || ad_high === undefined || ad_high === "") {
                    dispatch(change(FORM, "dir_germline_ad_high", 1));
                }
            }
        } else if (shouldApplyBand) {
            if (gtCodes[0] === "0/1") {
                dispatch(change(FORM, "dir_germline_ad_low", 0.2));
                dispatch(change(FORM, "dir_germline_ad_high", 0.8));
            } else if (gtCodes[0] === "1/1") {
                dispatch(change(FORM, "dir_germline_ad_low", 0.8));
                dispatch(change(FORM, "dir_germline_ad_high", 1));
            }
        }

        prevGtKeyRef.current = key;
    }, [gt_selection, dispatch, store]);

    const resetSection = () => {
        dispatch(initialize(FORM, { ...DIR_GERMLINE_GT_FORM_RESET }));
        prevGtKeyRef.current = "[]";
    };

    const gtCodes = normalizeGtCodes(gt_selection);
    const hasZygosity = gtCodes.length > 0;
    const isHetAndHomAlt = gtCodes.length > 1;
    const afLowDisabled = !hasZygosity;
    const afHighDisabled = !hasZygosity || isHetAndHomAlt;

    return (
        <div className={"form_section"}>
            <div className={"bar"}>
                <GPAP_Button_Light
                    size="sm"
                    variant={"gpap_button"}
                    disabled={pristine || submitting}
                    onClick={() => resetSection()}
                >
                    <MappedText text={"Clear Filters"} />
                </GPAP_Button_Light>
            </div>
            <form onSubmit={handleSubmit}>
                <div className={"form_container"}>
                    <SelectArea
                        collection_name={"gt_selection"}
                        header={"Germline zygosity"}
                        fields={GT_OPTIONS}
                    />
                    <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                        <MappedText
                            text={
                                "Restrict variants using the same sample fields as the analysis (gt, DP, GQ, AF low/high). Only applies in DIR germline view."
                            }
                        />
                    </p>
                    <Grid container spacing={1} style={{ marginTop: 12 }}>
                        <Grid item xs={12} sm={6}>
                            <Field
                                name="dir_germline_dp"
                                component={DirNumberField}
                                label={<MappedText text={"DP (min depth)"} />}
                                helperText={<MappedText text={"Minimum read depth per sample"} />}
                                inputProps={{ min: 0, step: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Field
                                name="dir_germline_gq"
                                component={DirNumberField}
                                label={<MappedText text={"GQ (min genotype quality)"} />}
                                helperText={<MappedText text={"Minimum genotype quality"} />}
                                inputProps={{ min: 0, step: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Field
                                name="dir_germline_ad_low"
                                component={DirNumberField}
                                disabled={afLowDisabled}
                                label={<MappedText text={"AF low (allele frequency)"} />}
                                helperText={
                                    !hasZygosity ? (
                                        <MappedText
                                            text={
                                                "Select at least one zygosity to set alternate allele frequency bounds."
                                            }
                                        />
                                    ) : isHetAndHomAlt ? (
                                        <MappedText
                                            text={
                                                "Minimum alternate allele frequency (0–1). Used for both 0/1 and 1/1 when both are selected."
                                            }
                                        />
                                    ) : (
                                        <MappedText text={"Lower bound for alt allele frequency (0–1)"} />
                                    )
                                }
                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Field
                                name="dir_germline_ad_high"
                                component={DirNumberField}
                                disabled={afHighDisabled}
                                label={<MappedText text={"AF high (allele frequency)"} />}
                                helperText={
                                    !hasZygosity ? (
                                        <MappedText
                                            text={
                                                "Select at least one zygosity to set alternate allele frequency bounds."
                                            }
                                        />
                                    ) : isHetAndHomAlt ? (
                                        <MappedText
                                            text={
                                                "Not used when 0/1 and 1/1 are both selected; only the minimum (AF low) is applied to the query."
                                            }
                                        />
                                    ) : (
                                        <MappedText text={"Upper bound for alt allele frequency (0–1)"} />
                                    )
                                }
                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                            />
                        </Grid>
                    </Grid>
                </div>
            </form>
        </div>
    );
}

export default reduxForm({
    form: FORM,
    destroyOnUnmount: false,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    updateUnregisteredFields: true,
    initialValues: { ...DIR_GERMLINE_GT_FORM_RESET }
})(connect((state) => ({ gt_selection: dirGermlineSelector(state, "gt_selection") }))(DirGermlineGtSection));
